import { utils } from '@/utils';
import { scopedChannelApi } from '@/session/scopedClients';
import type { RequestScope } from '@/session/scopedTransport';
import { ChannelApiResponse } from '@/types/endpoints';
import type { WriteRequestOptions } from '../requestOptions';

const validate = (subscriptionId: number, overrides: Override[]) => {
  if (!subscriptionId || subscriptionId < 1) {
    throw new Error('Valid subscription ID is required');
  }
  if (!overrides || overrides.length === 0) {
    throw new Error('At least one override is required');
  }
  overrides.forEach((override, index) => {
    if (!override.product_id || override.product_id < 1) {
      throw new Error(`Override ${index}: Valid product_id is required`);
    }
    if (!override.assignment_id || override.assignment_id < 1) {
      throw new Error(`Override ${index}: Valid assignment_id is required`);
    }
    if (override.manual_value === undefined || override.manual_value === null) {
      throw new Error(`Override ${index}: manual_value is required`);
    }
    // Allow empty strings for optional attributes (backend will validate based on min_occurs)
    if (override.manual_value.length > 5000) {
      throw new Error(`Override ${index}: manual_value exceeds 5000 characters`);
    }
    if (override.reason && override.reason.length > 1000) {
      throw new Error(`Override ${index}: reason exceeds 1000 characters`);
    }
  });
};

export interface Override {
  product_id: number;
  assignment_id: number;
  manual_value: string;
  reason?: string;
}

export interface ManualOverrideRequest {
  overrides: Override[];
  overridden_by?: string;
  user_id?: string;
  modified_by?: string;
}

export interface OverrideResult {
  product_id: number;
  assignment_id: number;
  manual_value: string;
  overridden_by: string;
  overridden_at: string;
  reason: string | null;
  authority_id: number;
  status: 'success';
}

export interface OverrideError {
  product_id: number;
  assignment_id: number;
  error: string;
}

export interface ManualOverrideResponse {
  subscription_id: number;
  processed_count: number;
  successful_count: number;
  error_count: number;
  results: OverrideResult[];
  errors?: OverrideError[];
  next_steps: {
    note: string;
    render_command: string;
  };
}

type RequestParams = {
  subscriptionId: number;
  overrides: Override[];
  overriddenBy: string;
};

export const manualOverride = async (
  scope: RequestScope,
  { subscriptionId, overrides, overriddenBy }: RequestParams,
  options: WriteRequestOptions
): Promise<ChannelApiResponse<ManualOverrideResponse>> => {
  try {
    validate(subscriptionId, overrides);

    const requestBody: ManualOverrideRequest = {
      overrides,
      overridden_by: overriddenBy,
    };

    const result = await scopedChannelApi(scope).post<ManualOverrideResponse>(`atts/acendaai/manual_override/${subscriptionId}`, requestBody, {
      ...options,
      headers: {
        'x-astur-subscription-id': subscriptionId.toString(),
      },
    });

    return {
      data: result.data,
      status: result.status,
    };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
};

// Delete Manual Override Types and Functions

const validateDelete = (subscriptionId: number, overrides: OverrideToDelete[]) => {
  if (!subscriptionId || subscriptionId < 1) {
    throw new Error('Valid subscription ID is required');
  }
  if (!overrides || overrides.length === 0) {
    throw new Error('At least one override is required');
  }
  overrides.forEach((override, index) => {
    if (!override.product_id || override.product_id < 1) {
      throw new Error(`Override ${index}: Valid product_id is required`);
    }
    if (!override.assignment_id || override.assignment_id < 1) {
      throw new Error(`Override ${index}: Valid assignment_id is required`);
    }
  });
};

export interface OverrideToDelete {
  product_id: number;
  assignment_id: number;
}

export interface DeleteManualOverrideRequest {
  overrides: OverrideToDelete[];
  deleted_by?: string;
  user_id?: string;
  modified_by?: string;
}

export interface DeleteResult {
  product_id: number;
  assignment_id: number;
  deleted_at: string;
  deleted_by: string;
  previous_value: string;
  authority_id: number;
  status: 'success';
}

export interface DeleteError {
  product_id: number;
  assignment_id: number;
  error: string;
}

export interface DeleteManualOverrideResponse {
  subscription_id: number;
  processed_count: number;
  successful_count: number;
  error_count: number;
  results: DeleteResult[];
  errors?: DeleteError[];
  next_steps: {
    note: string;
    render_command: string;
  };
}

type DeleteRequestParams = {
  subscriptionId: number;
  overrides: OverrideToDelete[];
  deletedBy: string;
};

export const deleteManualOverride = async (
  scope: RequestScope,
  { subscriptionId, overrides, deletedBy }: DeleteRequestParams,
  options: WriteRequestOptions
): Promise<ChannelApiResponse<DeleteManualOverrideResponse>> => {
  try {
    validateDelete(subscriptionId, overrides);

    const requestBody: DeleteManualOverrideRequest = {
      overrides,
      deleted_by: deletedBy,
    };

    const result = await scopedChannelApi(scope).delete<DeleteManualOverrideResponse>(`atts/acendaai/manual_override/${subscriptionId}`, {
      ...options,
      headers: {
        'x-astur-subscription-id': subscriptionId.toString(),
      },
      data: requestBody,
    });

    return {
      data: result.data,
      status: result.status,
    };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
};
